import { decodeText } from "../utility/decodeText.js";
import { decodeLogText } from "./decodeLogText.js";
import { parseLogText } from "./logText.js";
const logHeaderSize = 64;
/**
 * Reads the log block located at `flogoff`. The areas it announces are
 * clamped to the file; every anomaly is reported through the context.
 * @param buffer - SPC buffer (its endianness is used for the header fields).
 * @param logOffset - `flogoff` from the main header.
 * @param context - shared parse state (encoding and warnings).
 * @returns The log block, or `null` when there is none or it cannot be read.
 */
export function readLogBlock(buffer, logOffset, context) {
    if (logOffset === 0)
        return null;
    const fileLength = buffer.length;
    if (logOffset >= fileLength) {
        context.warn(`Log block offset ${logOffset} is beyond the end of the file (${fileLength} bytes); log ignored`);
        return null;
    }
    if (logOffset + logHeaderSize > fileLength) {
        context.warn(`Log block header at offset ${logOffset} is cut by the end of the file (${fileLength} bytes); log ignored`);
        return null;
    }
    try {
        return decodeLogBlock(buffer, logOffset, context);
    }
    catch (error) {
        if (context.strict)
            throw error;
        context.warn(`Log block could not be read (${error.message}); log ignored`);
        return null;
    }
}
/**
 * Returns a view over all the bytes of an SPC buffer.
 * @param buffer - SPC buffer.
 * @returns A `Uint8Array` over the same memory.
 */
export function getBytes(buffer) {
    return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
}
/**
 * Extracts the areas of a log block from its numeric header fields: binary
 * and disk areas after the 64-byte header, text at `textOffset`, all clamped
 * to the file, with a warning for every anomaly.
 * @param bytes - the whole file.
 * @param logOffset - where the 64-byte header layout starts.
 * @param fields - the numeric header fields.
 * @param context - shared parse state.
 * @returns The log block.
 */
export function decodeLogAreas(bytes, logOffset, fields, context) {
    const fileLength = bytes.length;
    const meta = {
        ...fields,
        reserved: decodeText(bytes.subarray(logOffset + 20, logOffset + logHeaderSize), { encoding: context.encoding }),
    };
    const { size, textOffset, binarySize, diskArea } = meta;
    const blockEnd = logOffset + Math.max(size, logHeaderSize + binarySize + diskArea);
    if (blockEnd > fileLength) {
        context.warn(`Log block ends ${blockEnd - fileLength} bytes beyond the end of the file; truncated`);
    }
    const binaryStart = logOffset + logHeaderSize;
    const diskStart = binaryStart + binarySize;
    const binary = bytes.slice(binaryStart, Math.min(diskStart, fileLength));
    const disk = bytes.slice(Math.min(diskStart, fileLength), Math.min(diskStart + diskArea, fileLength));
    let textStart = textOffset;
    let textEnd = logOffset + size;
    if (textStart < logHeaderSize) {
        context.warn(`Log text offset ${textOffset} points inside the log header; text read from offset ${logHeaderSize}`);
        textStart = logHeaderSize;
    }
    if (textStart > size) {
        context.warn(`Log text offset ${textOffset} is beyond the log block size ${size}; text read up to the end of the file`);
        textEnd = fileLength;
    }
    const start = Math.min(logOffset + textStart, fileLength);
    const end = Math.max(start, Math.min(textEnd, fileLength));
    const text = decodeLogText(bytes.subarray(start, end), context);
    return { meta, text, ...parseLogText(text), binary, disk };
}
/**
 * Decodes a log block whose 64-byte header lies inside the file.
 * @param buffer - SPC buffer.
 * @param logOffset - `flogoff` from the main header.
 * @param context - shared parse state.
 * @returns The log block.
 */
function decodeLogBlock(buffer, logOffset, context) {
    buffer.offset = logOffset;
    const fields = {
        size: buffer.readUint32(),
        memorySize: buffer.readUint32(),
        textOffset: buffer.readUint32(),
        binarySize: buffer.readUint32(),
        diskArea: buffer.readUint32(),
    };
    return decodeLogAreas(getBytes(buffer), logOffset, fields, context);
}
//# sourceMappingURL=logBlock.js.map